import { useId, useState, type ReactElement } from "react";
import { flushSync } from "react-dom";
import { createRoot } from "react-dom/client";
import "../ui/kit/styles.ts";
import { Button, ConfirmDialog, Dialog, KitInputProvider, type KitInput } from "../ui/kit/index.ts";
import { say } from "../words.ts";
import type { Ask, Question } from "./ask.ts";
import type { LogoutEverywhereOutcome } from "./auth.ts";

/**
 * The game page's confirmation before logging out everywhere (P1.4-T15): the kit's `ConfirmDialog` in a
 * React root of its own over the page, on the page's input router, so Escape cancels it through the router
 * and no key reaches the game or the login form behind it. The page hands it the request to run; the
 * dialog shows busy while the request is out and the server's sentence if it fails, and closes once it
 * succeeds or the player cancels. The wording is a draft for the author (ADR-064's provisional copy),
 * written in the pixel face's glyphs, and names no account: accounts have no name (ADR-064's amendment).
 * It says what the gateway does: every session of the account is revoked, so each must log in again, and a
 * socket already in the world is closed 4001 with it (ADR-075's amendment of 2026-10-27), so a tab playing now
 * shows Logged out.
 *
 * When the gateway answers that this tab's own session is already gone (401), nothing was ended anywhere
 * and confirming again can only fail the same way, so the confirm dialog gives way to a dialog that says
 * so plainly, as its accessible description, and offers one way out, Log in, which Escape takes too: the
 * page then returns to the log in form.
 */

/**
 * The dialog's width in logical pixels, which holds the centred title on one line in the panel-title face. Its
 * words are `account.logoutEverywhere.*` in the language files.
 */
export const LOGOUT_EVERYWHERE_WIDTH = 272;

/**
 * Asks the player to confirm logging out everywhere and runs the request once they do.
 *
 * @param run Sends the request and says how it went.
 * @returns True once the request has succeeded and the dialog has closed; false when the player cancelled,
 *   or closed the dialog after the gateway answered that this tab was already logged out, after the dialog
 *   has closed.
 */
export type ConfirmLogoutEverywhere = (run: () => Promise<LogoutEverywhereOutcome>) => Promise<boolean>;

/**
 * What the dialog shows once the gateway has answered that this tab was already logged out: the sentence,
 * which describes the dialog, and Log in, its one action and the first control it focuses.
 *
 * @param props `onLeave`, called by Log in and Escape.
 * @returns The dialog.
 * @throws Error when mounted outside a `KitInputProvider`.
 */
export function AlreadyLoggedOut({ onLeave }: { readonly onLeave: () => void }): ReactElement {
  const sentenceId = useId();
  return (
    <Dialog
      title={say("account.logoutEverywhere.title")}
      width={LOGOUT_EVERYWHERE_WIDTH}
      onClose={onLeave}
      describedBy={sentenceId}
      actions={
        <Button variant="primary" onClick={onLeave}>
          {say("account.logoutEverywhere.lostAction")}
        </Button>
      }
    >
      <p id={sentenceId} style={{ margin: 0 }}>
        {say("account.logoutEverywhere.lost")}
      </p>
    </Dialog>
  );
}

/**
 * The open dialog: busy while the request runs, then the server's sentence when it failed, or the dialog
 * that says this tab was already logged out.
 *
 * @param props `run` sends the request; `done` closes the dialog with whether the request succeeded.
 * @returns The confirm dialog, or the dialog that says this tab was already logged out.
 */
function Asking({ run, done }: { readonly run: () => Promise<LogoutEverywhereOutcome>; readonly done: (succeeded: boolean) => void }): ReactElement {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [lost, setLost] = useState(false);
  /**
   * Sends the request, keeping the dialog busy until it answers.
   */
  const confirm = (): void => {
    setBusy(true);
    setError(undefined);
    void run().then((outcome) => {
      if (outcome.ok) {
        done(true);
        return;
      }
      setBusy(false);
      if (outcome.lost) {
        setLost(true);
        return;
      }
      setError(outcome.message);
    });
  };
  if (lost) {
    return <AlreadyLoggedOut onLeave={() => done(false)} />;
  }
  return (
    <ConfirmDialog title={say("account.logoutEverywhere.title")} confirmLabel={say("account.logoutEverywhere.confirm")} cancelLabel={say("account.logoutEverywhere.cancel")} width={LOGOUT_EVERYWHERE_WIDTH} onConfirm={confirm} onCancel={() => done(false)} busy={busy} error={error}>
      <p style={{ margin: 0 }}>{say("account.logoutEverywhere.body")}</p>
    </ConfirmDialog>
  );
}

/**
 * The page's confirmations, sharing one root over the page: logging out everywhere, and any other question the page
 * asks before an action that leaves something (`Ask`), such as character select's Log out and the game menu's Change
 * character. One dialog shows at a time.
 */
export interface PageConfirms {
  readonly everywhere: ConfirmLogoutEverywhere;
  readonly ask: Ask;
  /**
   * Shows a dialog of the caller's until it answers, as `ask` shows a question: `draw` is given the function that
   * closes it with its answer. Resolves false at once while a dialog is open.
   */
  readonly show: (draw: (done: (answer: boolean) => void) => ReactElement) => Promise<boolean>;
}

/**
 * A question in the kit's confirm dialog, closing with the player's answer.
 *
 * @param props The `question` and `done`, called with whether the player confirmed.
 * @returns The dialog.
 */
function Asked({ question, done }: { readonly question: Question; readonly done: (confirmed: boolean) => void }): ReactElement {
  return (
    <ConfirmDialog title={question.title} confirmLabel={question.confirm} width={question.width} tone={question.tone} onConfirm={() => done(true)} onCancel={() => done(false)}>
      <p style={{ margin: 0 }}>{question.body}</p>
    </ConfirmDialog>
  );
}

/**
 * Makes the page's confirmations. The host is a positioned element over the page that takes no pointer events
 * itself; the dialog's scrim fills it and takes them while a dialog is open.
 *
 * @param host The element the dialog's scrim fills.
 * @param input The page's input router.
 * @returns The ways to open a dialog; a call while one is open resolves false at once.
 */
export function mountConfirms(host: HTMLElement, input: KitInput): PageConfirms {
  const root = createRoot(host);
  let open = false;

  /**
   * Shows one dialog until it answers, unless one is already open.
   *
   * @param draw Draws the dialog, given the function that closes it with its answer.
   * @returns The answer once the dialog has closed and given focus back, or false at once while one is open.
   */
  const show = (draw: (done: (answer: boolean) => void) => ReactElement): Promise<boolean> => {
    if (open) {
      return Promise.resolve(false);
    }
    open = true;
    return new Promise<boolean>((resolve) => {
      /**
       * Takes the dialog off the page, so it has given focus back before the page moves on, and answers.
       *
       * @param answer The dialog's answer.
       */
      const done = (answer: boolean): void => {
        flushSync(() => root.render(null));
        open = false;
        resolve(answer);
      };
      root.render(<KitInputProvider input={input}>{draw(done)}</KitInputProvider>);
    });
  };

  return {
    everywhere: (run) => show((done) => <Asking run={run} done={done} />),
    ask: (question) => show((done) => <Asked question={question} done={done} />),
    show,
  };
}
